import TitleHeader from "@/components/TitleHeader";
import { useNavigate } from "react-router-dom";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useUserInfo } from "@/context/UserInfo";
import QrBg from "@/pages/charge/assets/qr-bg.png";
import { useEffect, useMemo, useState, useRef } from "react";
import { getOrdersByRecipient, Order, OrderType, Status } from "@/api/order";
import { accAdd, formatAmountWithFixed } from "@/utils/formatBalance";
import { usdcInfo } from "@/constants/tokens";
import { useCurrencyExchange } from "@/hooks/useCurrencyExchange";
import { consumerEmoji } from "@/utils/avatars";
import { shortenAddress } from "@/utils";
import QRCodeStyling from "qr-code-styling";
import LogoL from "@/assets/logo-l.png";

const DirectPay = () => {
  const navigate = useNavigate();
  const { localConvertToUsd, currencyUnit, usdToCurrency } =
    useCurrencyExchange();
  const { orgName, senderAddress, avatarIndex } = useUserInfo();
  const [currentTime] = useState(new Date()); //new Date() "2024-12-25T09:40:19.491Z"
  const [orderList, setOrderList] = useState<Order[]>([]);
  const [totalAmount, setTotalAmount] = useState("0");
  const qrRef = useRef<HTMLDivElement>(null);

  const getDirectPayOrder = async () => {
    console.log(currentTime);
    const response = await getOrdersByRecipient({
      recipient: senderAddress,
      status: Status.Completed,
      orderType: OrderType.DirectPay,
    });

    const list = response.filter((item) => {
      return (
        new Date(item.updatedAt).getTime() > new Date(currentTime).getTime()
      );
    });
    setOrderList(list);
    setTotalAmount(
      list.reduce((sum, item) => accAdd(sum, item.orderAmount), "0")
    );
  };

  const qrValue = useMemo(() => {
    if (!senderAddress) return "";
    const url = `${process.env.REACT_APP_URL}/direct/`;
    return `${url}${senderAddress}`;
  }, [senderAddress]);

  useEffect(() => {
    if (!senderAddress) return;
    getDirectPayOrder();

    const timer = setInterval(() => {
      getDirectPayOrder();
    }, 3000);

    return () => clearTimeout(timer);
  }, [senderAddress]);

  useEffect(() => {
    if (!qrValue || !qrRef || !qrRef.current) return;

    const timer = setTimeout(() => {
      const qrCode = new QRCodeStyling({
        width: 480,
        height: 480,
        data: qrValue,
        dotsOptions: {
          color: "#000000",
          type: "square",
        },
        cornersSquareOptions: {
          color: "#000000",
          type: "extra-rounded",
        },
        backgroundOptions: {
          color: "#FFFFFF",
        },
        image: LogoL,
        imageOptions: {
          crossOrigin: "anonymous",
          margin: 0,
          imageSize: 0.4,
          hideBackgroundDots: true,
        },
      });

      qrRef!.current!.innerHTML = "";
      qrCode.append(qrRef!.current!);
      const canvas = qrRef!.current!.querySelector("canvas");
      if (canvas) {
        canvas.style.width = "240px";
        canvas.style.height = "240px";
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [qrValue]);

  return (
    <Flex
      sx={{
        padding: "20px",
      }}
      flexDirection={"column"}
      alignItems={"center"}
    >
      <TitleHeader
        onBack={() => navigate("/")}
        title="Direct Pay"
        color="#D8762B"
        showSwitch={true}
      />
      <Text
        sx={{
          color: "#333",
          textAlign: "center",
          fontSize: "20px",
          marginTop: "20px",
          fontWeight: 500,
        }}
      >
        {orgName}
      </Text>

      <Box
        sx={{
          width: "260px",
          height: "370px",
          margin: "15px auto 0",
          background: `url(${QrBg}) no-repeat center center`,
          backgroundSize: "cover",
          padding: "30px 15px 0",
        }}
      >
        <Flex ref={qrRef} style={{ margin: "0 auto" }} justify={"center"} />

        <Text
          sx={{
            color: "#333",
            fontSize: "14px",
            fontWeight: 700,
            marginTop: "12px",
            textAlign: "center",
          }}
        >
          Please Scan this QR code to pay
        </Text>
      </Box>
      {
        <Box
          sx={{
            width: "230px",
            margin: "15px auto 0",
          }}
        >
          <Flex alignItems={"center"} justifyContent={"space-between"}>
            <Text
              sx={{
                color: "#848484",
                fontSize: "14px",
                fontWeight: 700,
              }}
            >
              Total
            </Text>
            <Text
              sx={{
                fontSize: "18px",
                fontWeight: 700,
              }}
            >
              +
              {formatAmountWithFixed(
                usdToCurrency(totalAmount),
                usdcInfo.decimals
              )}{" "}
              {currencyUnit}
            </Text>
          </Flex>
          {orderList.map((item, index) => {
            return (
              <Flex
                key={index}
                alignItems={"center"}
                sx={{
                  padding: "10px 0",
                  borderBottom: "1px solid #E5E5E5",
                }}
              >
                <Text
                  sx={{
                    fontSize: "30px",
                  }}
                >
                  {consumerEmoji}
                </Text>
                <Text
                  sx={{
                    color: "#333",
                    fontSize: "14px",
                    fontWeight: 500,
                    marginLeft: "10px",
                  }}
                >
                  {shortenAddress(item.sender?.address || "")}
                </Text>
                <Text
                  sx={{
                    fontSize: "16px",
                    fontWeight: 700,
                    marginLeft: "auto",
                    color: "#00A54D",
                  }}
                >
                  +
                  {formatAmountWithFixed(
                    usdToCurrency(item.orderAmount),
                    usdcInfo.decimals
                  )}{" "}
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: 700,
                    }}
                  >
                    {" "}
                    {currencyUnit}
                  </span>
                </Text>
              </Flex>
            );
          })}
        </Box>
      }
    </Flex>
  );
};

export default DirectPay;
